import Card from "components/card/Card";
// import DropFilter from "components/dropdown/DropFilter";
import Spin from "components/spin/spin";
import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IBookingCategory } from "redux/reducers/models/teetime";
import "./bookingType.scss";

interface IBookingTypesCounts {
  booking_category_id: number;
  booking_category_name: string;
  participant_count: number;
  percentage: number;
}

interface IProps {
  bookingTypesCounts: Array<IBookingTypesCounts>;
  /**Determines if the legend is displayed to the right of the chart */
  legend?: boolean;
  /**Size of the chart (svg). Defaults to 200 */
  size?: number;
  bookingCategories: Array<IBookingCategory>;
}

export const DASHBOARD_COLOURS = [
  "#7357D6",
  "#1BAA5A",
  "#F73952",
  "#C6B7F7",
  "#B846C3",
  "#E9ACE8",
  "#1B94FD",
  "#88C6F7",
  "#2CDFFD",
  "#5BCEEC",
  "#87CE9B",
  "#FEAB2D",
  "#FAC35B",
  "#FE2889",
  "#F7A8C9",
  "#F6A7A7",
];

export default function BookingType(props: IProps) {
  const { bookingTypesCounts, size = 200, legend = true, bookingCategories } = props;
  const { t } = useTranslation();
  const radius = size / 2;
  const [filterIds] = useState<Array<number>>([]);

  let cumulativePercent = 0;

  const getCoordinatesForPercent = (percent: number) => {
    const angle = 2 * Math.PI * percent;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    return [x, y];
  };

  const innerRadius = radius * 0.5; // Thickness of the doughnut (0 to have no inner circle)

  const slices = useMemo(() => {
    return bookingTypesCounts
      ?.filter(filter => (filterIds?.length > 0 ? filterIds?.includes(filter?.booking_category_id) : true))
      ?.map((category, index) => {
        const total = bookingTypesCounts
          ?.filter(filter => (filterIds?.length > 0 ? filterIds?.includes(filter?.booking_category_id) : true))
          ?.reduce((sum, item) => sum + item.participant_count, 0);
        const value = category?.participant_count;
        const percent = value / total;

        const [startOuterX, startOuterY] = getCoordinatesForPercent(cumulativePercent);
        const [startInnerX, startInnerY] = [startOuterX * (innerRadius / radius), startOuterY * (innerRadius / radius)];

        cumulativePercent += percent;

        const [endOuterX, endOuterY] = getCoordinatesForPercent(cumulativePercent);
        const [endInnerX, endInnerY] = [endOuterX * (innerRadius / radius), endOuterY * (innerRadius / radius)];

        const largeArcFlag = percent > 0.5 ? 1 : 0;

        const pathData = [
          // Outer arc
          `M ${startOuterX} ${startOuterY}`,
          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${endOuterX} ${endOuterY}`,
          // Line to inner end
          `L ${endInnerX} ${endInnerY}`,
          // Inner arc back
          `A ${innerRadius} ${innerRadius} 0 ${largeArcFlag} 0 ${startInnerX} ${startInnerY}`,
          "Z",
        ].join(" ");

        return (
          <path
            key={category?.booking_category_id}
            d={pathData}
            fill={DASHBOARD_COLOURS[index % DASHBOARD_COLOURS?.length]}
            stroke="#FFFFFF" // Outline colour
            strokeWidth={0} // Outline width
          >
            <title>{`${category?.booking_category_name}: ${category?.participant_count} (${(percent * 100).toFixed(
              1,
            )}%)`}</title>
          </path>
        );
      });
  }, [bookingTypesCounts, filterIds]);

  //Show empty chart
  function emptyChart() {
    const [startOuterX, startOuterY] = getCoordinatesForPercent(100);
    const [startInnerX, startInnerY] = [startOuterX * (innerRadius / radius), startOuterY * (innerRadius / radius)];
    const [endOuterX, endOuterY] = getCoordinatesForPercent(cumulativePercent);
    const [endInnerX, endInnerY] = [endOuterX * (innerRadius / radius), endOuterY * (innerRadius / radius)];
    const pathData = [
      // Outer arc
      `M ${startOuterX} ${startOuterY}`,
      `A ${radius} ${radius} 0 1 1 ${endOuterX} ${endOuterY}`,
      // Line to inner end
      `L ${endInnerX} ${endInnerY}`,
      // Inner arc back
      `A ${innerRadius} ${innerRadius} 0 1 0 ${startInnerX} ${startInnerY}`,
      "Z",
    ].join(" ");
    return (
      <path
        d={pathData}
        fill={"#EAECF0"}
        stroke="#FFFFFF" // Outline colour
        strokeWidth={0} // Outline width
      >
        <title>0%</title>
      </path>
    );
  }

  //   function handleExtendedFilter(filterReturn: Array<Record<string, any>>) {
  //     setFilterIds(filterReturn?.map(filter => filter?.value));
  //   }

  return (
    <Card
      title={t("Booking Types")}
      titleDetails={[
        {
          content: bookingTypesCounts
            ? //     <DropFilter
              //     title={t("Booking Types")}
              //     filterData={bookingTypesCounts?.map(category => ({value: category?.booking_category_id, title: category?.booking_category_name}))}
              //     filterLabelPropFromData="title"
              //     filterIdPropFromData="value"
              //     filterType="Checkbox"
              //     applyFilters={(filter) => handleExtendedFilter(filter)}
              //     save
              //     historyKey="booking_types_filter"
              //     rightAligned
              //   />
              ""
            : "",
        },
      ]}
    >
      <Card.Section>
        {bookingTypesCounts ? (
          <div className="ui-booking-type">
            <svg width={size} height={size} viewBox={`${-radius} ${-radius} ${size} ${size}`}>
              {slices?.length > 0 && bookingTypesCounts?.some(count => count?.participant_count > 0)
                ? slices
                : emptyChart()}
            </svg>
            {/* Legend */}
            {legend && bookingTypesCounts?.length > 0 ? (
              <ul className="ui-booking-type-list">
                {bookingTypesCounts
                  ?.filter(filter => (filterIds?.length > 0 ? filterIds?.includes(filter?.booking_category_id) : true))
                  ?.map((category, i) => (
                    <li key={category?.booking_category_id}>
                      <span
                        style={{
                          backgroundColor: DASHBOARD_COLOURS[i % DASHBOARD_COLOURS.length],
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                        }}
                      />
                      <span>
                        {category?.booking_category_name}: {category?.participant_count} ({category?.percentage}%)
                      </span>
                    </li>
                  ))}
              </ul>
            ) : legend && (!bookingTypesCounts || bookingTypesCounts?.length === 0) ? (
              <ul className="ui-booking-type-list">
                {bookingCategories
                  ?.filter(filter => (filterIds?.length > 0 ? filterIds?.includes(filter?.id) : true))
                  ?.map((category, i) => (
                    <li key={category?.id}>
                      <span
                        style={{
                          backgroundColor: DASHBOARD_COLOURS[i % DASHBOARD_COLOURS.length],
                          display: "inline-block",
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                        }}
                      />
                      <span>{category?.title}: 0%</span>
                    </li>
                  ))}
              </ul>
            ) : null}
          </div>
        ) : (
          <div style={{ height: "40px", display: "flex", width: "40px", margin: "auto" }}>
            <Spin />
          </div>
        )}
      </Card.Section>
    </Card>
  );
}
